import {useEffect, useState, useCallback, useRef} from 'react';
import {useParams, Link} from 'react-router-dom';
import DOMPurify from 'dompurify';
import {getRepository} from '../data/repository';
import type {FinancialYear, Profile, Document} from '../types/vault';
import {ModalDrawer} from '../components/ModalDrawer';
import {FinancialYearForm} from '../components/FinancialYearForm';
import {TaxTrendSection} from '../components/TaxTrendSection';
import {DocumentPreview, DocumentHoverPreview} from '../components/DocumentPreview';
import {formatAUD, formatFY, formatAustralianCalendarDate} from '../utils/clipboard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiChevronLeft, FiEdit2, FiCalendar, FiFileText, FiAlertCircle,
  FiRefreshCw, FiEye, FiDownload, FiCheck, FiLock
} = FiIcons;

function SummaryCard({title, value, textColor = 'text-slate-900', isOutcome = false}: {
  title: string; value: React.ReactNode; textColor?: string; isOutcome?: boolean
}) {
  return (
    <div className={`p-5 rounded-2xl shadow-sm border flex flex-col justify-center ${isOutcome ? 'border-[#0F766E]/20 bg-slate-50/50' : 'bg-white border-slate-100'}`}>
      <h3 className="text-sm font-medium text-slate-500 mb-1">{title}</h3>
      <p className={`text-2xl font-semibold tracking-tight ${textColor}`}>{value}</p>
    </div>
  );
}

function SecondaryRow({label, value}: {label: string; value: number | null | undefined}) {
  const isZero = value === 0;
  const isNull = value === null || value === undefined;
  const display = isNull ? '—' : formatAUD(value);
  const color = isNull || isZero ? 'text-slate-400' : 'text-slate-900';
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`text-sm font-medium ${color}`}>{display}</span>
    </div>
  );
}

export function FinancialYearDetail() {
  const {profileId, financialYearId} = useParams<{profileId: string; financialYearId: string}>();

  const [year, setYear] = useState<FinancialYear | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [allYears, setAllYears] = useState<FinancialYear[]>([]);
  const [allProfiles, setAllProfiles] = useState<Profile[]>([]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  // Hover preview state
  const [hoverPreview, setHoverPreview] = useState<{
    docId: string; anchorRect: DOMRect; isPinned: boolean
  } | null>(null);

  const previewOpenTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const previewCloseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const supportsHoverRef = useRef(window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  const role = getRepository().getCurrentUser()?.role || 'read_only';
  const canWrite = role === 'owner' || role === 'member';

  const loadData = useCallback(async () => {
    if (!profileId || !financialYearId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    const repo = getRepository();
    try {
      const [y, p, docs, yearsResponse, profiles] = await Promise.all([
        repo.getFinancialYear(financialYearId),
        repo.getProfile(profileId),
        repo.listDocuments({profileId}),
        repo.listFinancialYears(profileId),
        repo.listProfiles(),
      ]);
      if (!y || !p || y.profileId !== profileId) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setYear(y);
      setProfile(p);
      setDocuments(docs.filter(d => d.financialYearId === financialYearId));
      setAllYears(yearsResponse);
      setAllProfiles(profiles);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load financial year data');
    } finally {
      setLoading(false);
    }
  }, [profileId, financialYearId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    return () => {
      clearTimeout(previewOpenTimerRef.current);
      clearTimeout(previewCloseTimerRef.current);
    };
  }, []);

  const handleSave = async (updated: FinancialYear) => {
    await getRepository().saveFinancialYear(updated);
    setEditing(false);
    loadData();
  };

  const handleDownload = async (doc: Document) => {
    if (downloadingDocId) return;
    setDownloadError(null);
    setDownloadingDocId(doc.id);
    try {
      const {blob, filename} = await getRepository().downloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    } finally {
      setDownloadingDocId(null);
    }
  };

  // --- Hover preview handlers ---
  const handleEyeHoverEnter = (docId: string, e: React.MouseEvent) => {
    if (!supportsHoverRef.current) return;
    clearTimeout(previewCloseTimerRef.current);
    clearTimeout(previewOpenTimerRef.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    previewOpenTimerRef.current = setTimeout(() => {
      setHoverPreview({docId, anchorRect: rect, isPinned: false});
    }, 500);
  };

  const handleEyeHoverLeave = () => {
    clearTimeout(previewOpenTimerRef.current);
    previewCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(prev => (prev?.isPinned ? prev : null));
    }, 350);
  };

  const handleEyeFocus = (docId: string, e: React.FocusEvent) => {
    if (!supportsHoverRef.current) return;
    clearTimeout(previewCloseTimerRef.current);
    clearTimeout(previewOpenTimerRef.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHoverPreview({docId, anchorRect: rect, isPinned: false});
  };

  const handleEyeBlur = () => {
    clearTimeout(previewOpenTimerRef.current);
    previewCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(prev => (prev?.isPinned ? prev : null));
    }, 350);
  };

  const keepHoverPreviewOpen = () => {
    clearTimeout(previewCloseTimerRef.current);
  };

  const scheduleHoverPreviewClose = () => {
    clearTimeout(previewOpenTimerRef.current);
    previewCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(prev => (prev?.isPinned ? prev : null));
    }, 350);
  };

  const toggleHoverPreviewPin = () => {
    setHoverPreview(prev => (prev ? {...prev, isPinned: !prev.isPinned} : null));
  };

  const closeHoverPreview = () => {
    clearTimeout(previewOpenTimerRef.current);
    clearTimeout(previewCloseTimerRef.current);
    setHoverPreview(null);
  };

  const openHoverPreviewFull = () => {
    setHoverPreview(prev => {
      if (prev) setPreviewDocId(prev.docId);
      return null;
    });
    clearTimeout(previewOpenTimerRef.current);
    clearTimeout(previewCloseTimerRef.current);
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse space-y-6">
        <div className="h-6 bg-slate-200 rounded w-48" />
        <div className="h-40 bg-slate-200 rounded-2xl" />
        <div className="h-64 bg-slate-200 rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-400" />
        <h2 className="text-xl font-semibold text-slate-900">Unable to Load Record</h2>
        <p className="text-slate-500 max-w-sm">{error}</p>
        <button onClick={loadData} className="inline-flex items-center px-4 py-2 bg-[#0B2138] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" /> Retry
        </button>
      </div>
    );
  }

  if (notFound || !year || !profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-900">Record Not Found</h2>
        <p className="text-slate-500 max-w-sm">The financial year record you requested does not exist or the profile is invalid.</p>
        <Link to={profileId ? `/profiles/${profileId}` : '/profiles'} className="text-sm font-medium text-[#0F766E] hover:underline">
          Back to Profile
        </Link>
      </div>
    );
  }

  const renderOutcomeCard = () => {
    if ((year.taxRefund ?? 0) > 0) {
      return <SummaryCard title="Tax Refund" value={formatAUD(year.taxRefund)} textColor="text-green-600" isOutcome />;
    } else if ((year.taxPayable ?? 0) > 0) {
      return <SummaryCard title="Amount Payable" value={formatAUD(year.taxPayable)} textColor="text-amber-600" isOutcome />;
    }
    return <SummaryCard title="Tax Outcome" value="No balance" textColor="text-slate-400" isOutcome />;
  };

  return (
    <div className="space-y-6">
      <Link to={`/profiles/${profileId}`} className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#0F766E]">
        <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" /> Back to {profile.displayName}
      </Link>

      {/* Header */}
      <header className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium text-slate-500">{profile.displayName}</p>
            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#0F766E]/10 text-[#0F766E]">{profile.profileType}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{formatFY(year.endingYear)}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${year.status === 'Finalised' ? 'bg-[#0F766E]/10 text-[#0F766E]' : 'bg-slate-100 text-slate-700'}`}>
            {year.status}
          </span>
          {canWrite && (
            <button onClick={() => setEditing(true)} className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0F766E] whitespace-nowrap">
              <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-2" /> Edit Year
            </button>
          )}
        </div>
      </header>

      {/* Primary Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard title="Gross Income" value={formatAUD(year.grossIncome)} />
        <SummaryCard title="Taxable Income" value={formatAUD(year.taxableIncome)} />
        <SummaryCard title="Tax Withheld" value={formatAUD(year.taxWithheld)} />
        {renderOutcomeCard()}
      </div>

      {/* Grouped Secondary Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Panel 1: Tax Position & Adjustments */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-[#0F766E] mr-2" />
              <h2 className="text-lg font-medium text-slate-900">Tax Position & Adjustments</h2>
            </div>
            <div className="px-6 py-2">
              <SecondaryRow label="Tax Refund" value={year.taxRefund} />
              <SecondaryRow label="Amount Payable" value={year.taxPayable} />
              <SecondaryRow label="Capital Gain/Loss" value={year.capitalGainLoss} />
              <SecondaryRow label="Carry-Forward Loss" value={year.carryForwardLoss} />
              <SecondaryRow label="Super Contributions" value={year.superContributions} />
              <SecondaryRow label="Accountant Fee" value={year.accountantFee} />
            </div>
          </section>

          {/* Panel 2: Key Dates */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-medium text-slate-900">Key Dates</h2>
            </div>
            <div className="px-6 py-2">
              <div className="flex items-center justify-between py-3 border-b border-slate-50">
                <span className="text-sm text-slate-600">Lodgement Date</span>
                <span className={`text-sm font-medium ${year.lodgementDate ? 'text-slate-900' : 'text-slate-400'}`}>
                  {formatAustralianCalendarDate(year.lodgementDate)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-slate-600">Assessment Date</span>
                <span className={`text-sm font-medium ${year.assessmentDate ? 'text-slate-900' : 'text-slate-400'}`}>
                  {formatAustralianCalendarDate(year.assessmentDate)}
                </span>
              </div>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          {/* Panel 3: Annual Summary */}
          {year.annualSummary && (
            <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="text-lg font-medium text-slate-900">Annual Summary</h2>
              </div>
              <div className="px-6 py-4 overflow-x-auto">
                <div className="mfv-rich-content" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(year.annualSummary)}} />
              </div>
            </section>
          )}

          {/* Panel 4: Related Documents */}
          {documents.length > 0 && (
            <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center">
                <SafeIcon icon={FiFileText} className="w-5 h-5 text-[#102D4C] mr-2" />
                <h2 className="text-lg font-medium text-slate-900">Related Documents</h2>
              </div>

              {downloadError && (
                <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                  <p className="text-xs text-red-700">{downloadError}</p>
                  <button onClick={() => setDownloadError(null)} className="text-xs text-red-600 hover:text-red-800 font-medium ml-2 shrink-0">
                    Dismiss
                  </button>
                </div>
              )}

              {/* Desktop table layout */}
              <div className="hidden sm:block divide-y divide-slate-50">
                {documents.map(doc => (
                  <div key={doc.id} className="px-6 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => setPreviewDocId(doc.id)}
                        className="text-sm font-medium text-slate-900 hover:text-[#0F766E] text-left truncate block w-full focus:outline-none focus:underline"
                        title={`Preview ${doc.title}`}
                      >
                        {doc.title}
                      </button>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-xs text-slate-500">{doc.documentType}</span>
                        {doc.final && (
                          <span className="inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                            <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 mr-0.5" />Final
                          </span>
                        )}
                        {doc.sensitive && (
                          <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">
                            <SafeIcon icon={FiLock} className="w-2.5 h-2.5 mr-0.5" />Sensitive
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => {setPreviewDocId(doc.id); closeHoverPreview();}}
                        onMouseEnter={e => handleEyeHoverEnter(doc.id, e)}
                        onMouseLeave={handleEyeHoverLeave}
                        onFocus={e => handleEyeFocus(doc.id, e)}
                        onBlur={handleEyeBlur}
                        aria-label={`Preview ${doc.title}`}
                        title="Preview"
                        className="p-2 rounded-lg text-slate-400 hover:text-[#0F766E] hover:bg-[#0F766E]/10 focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {e.stopPropagation(); handleDownload(doc);}}
                        disabled={downloadingDocId === doc.id}
                        aria-label={`Download ${doc.title}`}
                        title="Download"
                        className="p-2 rounded-lg text-slate-400 hover:text-[#102D4C] hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors disabled:opacity-50"
                      >
                        {downloadingDocId === doc.id ? (
                          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                        ) : (
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    <span className="text-xs text-slate-400 shrink-0 w-20 text-right">
                      {new Date(doc.created).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Mobile card layout */}
              <div className="sm:hidden divide-y divide-slate-100">
                {documents.map(doc => (
                  <div key={doc.id} className="px-4 py-3.5 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <button
                          onClick={() => setPreviewDocId(doc.id)}
                          className="text-sm font-medium text-slate-900 hover:text-[#0F766E] text-left truncate block w-full focus:outline-none focus:underline"
                        >
                          {doc.title}
                        </button>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <span className="text-xs text-slate-500">{doc.documentType}</span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-400">{new Date(doc.created).toLocaleDateString()}</span>
                          {doc.final && (
                            <span className="inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                              <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 mr-0.5" />Final
                            </span>
                          )}
                          {doc.sensitive && (
                            <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">
                              <SafeIcon icon={FiLock} className="w-2.5 h-2.5 mr-0.5" />Sensitive
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPreviewDocId(doc.id)}
                        aria-label={`Preview ${doc.title}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium text-[#0F766E] bg-[#0F766E]/5 hover:bg-[#0F766E]/10 focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors flex-1 justify-center"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                        <span>Preview</span>
                      </button>
                      <button
                        onClick={(e) => {e.stopPropagation(); handleDownload(doc);}}
                        disabled={downloadingDocId === doc.id}
                        aria-label={`Download ${doc.title}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium text-[#102D4C] bg-slate-100 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0F766E] transition-colors flex-1 justify-center disabled:opacity-50"
                      >
                        {downloadingDocId === doc.id ? (
                          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                        ) : (
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        )}
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Tax Position Over Time */}
      <TaxTrendSection profileName={profile.displayName} years={allYears} />

      {canWrite && (
        <ModalDrawer isOpen={editing} onClose={() => setEditing(false)} title="Edit Financial Year">
          <FinancialYearForm year={year} profileId={profile.id} onSave={handleSave} onCancel={() => setEditing(false)} />
        </ModalDrawer>
      )}

      {/* Hover Preview */}
      {hoverPreview && (() => {
        const doc = documents.find(d => d.id === hoverPreview.docId);
        if (!doc) return null;
        return (
          <DocumentHoverPreview
            document={doc}
            visible={true}
            anchorRect={hoverPreview.anchorRect}
            profileName={profile?.displayName}
            yearLabel={formatFY(year.endingYear)}
            isPinned={hoverPreview.isPinned}
            onMouseEnter={keepHoverPreviewOpen}
            onMouseLeave={scheduleHoverPreviewClose}
            onTogglePin={toggleHoverPreviewPin}
            onOpenFull={openHoverPreviewFull}
            onClose={closeHoverPreview}
          />
        );
      })()}

      <DocumentPreview
        documentId={previewDocId}
        isOpen={!!previewDocId}
        onClose={() => setPreviewDocId(null)}
        profiles={allProfiles}
        financialYears={allYears}
      />
    </div>
  );
}